import { singletonPeerRevisions } from "@/lib/utils/connections/singleton-peers";
import { useOperationIntent } from "./use-operation-intent";
import {
  findOwnedConnection,
  findOwnedConnections,
} from "@/lib/utils/connections/find-owned-connection";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createConnectionV4,
  deleteConnectionV4,
  replayPendingConnectionV4,
  replaceConnectionV4,
  updateConnectionV4,
} from "@/lib/data/requests/connections/connection";
import { queryKeys } from "@/lib/data/static/query-keys";
import type {
  APIError,
  ConnectionCreate,
  ConnectionReplace,
  ConnectionUpdate,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

const requireOwnedConnection = (
  queryClient: ReturnType<typeof useQueryClient>,
  matterId: string,
  connectionId: string,
) => {
  for (const [, value] of queryClient.getQueriesData({
    queryKey: [queryKeys.matters, matterId],
  })) {
    const connection = findOwnedConnection(value, connectionId);
    if (connection) return { ...connection, matterId };
  }
  throw new Error("Reload the owning object before editing this connection.");
};

type MatterConnectionMutationContext = {
  matterId: string;
  operationId?: string;
};

const invalidateMatter = async (
  queryClient: ReturnType<typeof useQueryClient>,
  matterId: string,
) => {
  await queryClient.invalidateQueries({
    predicate: (query) => !isCanonicalSearchQuery(query),
    queryKey: [queryKeys.matters, matterId],
  });
};

export const useCreateConnection = () => {
  const queryClient = useQueryClient();
  const intent = useOperationIntent();

  return useMutation({
    ...intent,
    mutationFn: ({
      matterId,
      ...data
    }: ConnectionCreate & MatterConnectionMutationContext) => {
      return createConnectionV4({ ...data, matterId });
    },
    onError: (error: AxiosError<APIError>, variables) => {
      intent.onError(error, variables);
      toastAPIErrorMessage(error, "Error creating connection");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await invalidateMatter(queryClient, variables.matterId);
    },
  });
};

export const useReplaceConnection = () => {
  const queryClient = useQueryClient();
  const intent = useOperationIntent();

  return useMutation({
    ...intent,
    mutationFn: ({
      connectionId,
      matterId,
      ...data
    }: ConnectionReplace &
      MatterConnectionMutationContext & { connectionId: string }) => {
      const retry = replayPendingConnectionV4(data.operationId);
      if (retry) return retry;
      const current = requireOwnedConnection(
        queryClient,
        matterId,
        connectionId,
      );
      return replaceConnectionV4(
        connectionId,
        {
          ...data,
          peerRevisions: singletonPeerRevisions(
            current,
            queryClient
              .getQueriesData({
                queryKey: [queryKeys.matters, matterId],
              })
              .flatMap(([, value]) => findOwnedConnections(value)),
          ),
        },
        current,
      );
    },
    onError: (error: AxiosError<APIError>, variables) => {
      intent.onError(error, variables);
      toastAPIErrorMessage(error, "Error replacing connection");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await invalidateMatter(queryClient, variables.matterId);
    },
  });
};

export const useUpdateConnection = () => {
  const queryClient = useQueryClient();
  const intent = useOperationIntent();

  return useMutation({
    ...intent,
    mutationFn: ({
      connectionId,
      matterId,
      ...data
    }: ConnectionUpdate &
      MatterConnectionMutationContext & { connectionId: string }) => {
      return (
        replayPendingConnectionV4(data.operationId) ??
        updateConnectionV4(
          connectionId,
          data,
          requireOwnedConnection(queryClient, matterId, connectionId),
        )
      );
    },
    onError: (error: AxiosError<APIError>, variables) => {
      intent.onError(error, variables);
      toastAPIErrorMessage(error, "Error updating connection");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await invalidateMatter(queryClient, variables.matterId);
    },
  });
};

export const useDeleteConnection = () => {
  const queryClient = useQueryClient();
  const intent = useOperationIntent();

  return useMutation({
    ...intent,
    mutationFn: async ({
      connectionId,
      matterId,
      operationId,
    }: MatterConnectionMutationContext & { connectionId: string }) => {
      const retry = replayPendingConnectionV4(operationId);
      if (retry) return retry;
      const current = requireOwnedConnection(
        queryClient,
        matterId,
        connectionId,
      );
      return deleteConnectionV4(
        current,
        matterId,
        operationId,
        singletonPeerRevisions(
          current,
          queryClient
            .getQueriesData({ queryKey: [queryKeys.matters, matterId] })
            .flatMap(([, value]) => findOwnedConnections(value)),
        ),
      );
    },
    onError: (error: AxiosError<APIError>, variables) => {
      intent.onError(error, variables);
      toastAPIErrorMessage(error, "Error deleting connection");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await invalidateMatter(queryClient, variables.matterId);
    },
  });
};
